import { useEffect } from "react";

import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { Button } from "@ps-design-system/components/Button";
import { Text } from "@ps-design-system/components/Text";
import XIcon from "@ps-design-system/icons/X/X24";

import { trackButtonClicked } from "../../analytics/shared-analytics";
import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import {
  trackHostHubCalloutClicked,
  trackHostHubCalloutDismissed,
  trackHostHubCalloutViewed,
} from "../analytics/host-hub-analytics";
import type { MarketingBannerConfig } from "../utils/host-hub-marketing-banners.utils";

// Source asset is ~25MB; always request a resized, auto-format rendition.
const FSV_HOST_GUIDE_IMAGE_BASE =
  "https://img.peerspace.com/image/upload/f_auto,q_auto";
const FSV_HOST_GUIDE_IMAGE_ID = "fsv-host-hub-lvpdlq";

// Lowercased CTA copy, matching mobile's `button clicked` naming.
const CTA_BUTTON_NAME = "start increasing booking revenue";

interface FSVHostGuideBannerProps {
  callout: MarketingBannerConfig;
  dismiss: () => void;
}

const FSVHostGuideBanner = ({ callout, dismiss }: FSVHostGuideBannerProps) => {
  const ssoid = useSelector(selectProfileSSOid);

  useEffect(() => {
    trackHostHubCalloutViewed({
      calloutType: callout.type,
      ssoid,
    });
  }, [callout.type, ssoid]);

  const handleCtaClick = () => {
    trackHostHubCalloutClicked({
      calloutType: callout.type,
      ssoid,
    });
    trackButtonClicked({
      buttonLocation: "host hub",
      buttonName: CTA_BUTTON_NAME,
    });
    window.open(
      "https://go.peerspace.com/host-guide",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleDismiss = () => {
    trackHostHubCalloutDismissed({
      calloutType: callout.type,
      ssoid,
    });
    dismiss();
  };

  return (
    <div
      className="tw-relative tw-flex tw-flex-col tw-gap-[20px] md:tw-gap-[24px] tw-overflow-hidden tw-rounded-[12px] tw-p-[24px] tw-text-brand-white md:tw-flex-col-reverse lg:tw-flex-row lg:tw-items-center lg:tw-gap-[32px] lg:tw-p-[32px]"
      style={{
        background:
          "linear-gradient(249deg, rgba(0, 0, 0, 0.55) 0.49%, rgba(0, 0, 0, 0.00) 45.81%), linear-gradient(78deg, rgba(0, 0, 0, 0.55) 12.58%, rgba(0, 0, 0, 0.00) 81.3%), linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), var(--purple-purple-900, #5627B0)",
      }}
    >
      <button
        aria-label="Dismiss FSV host guide banner"
        className="tw-absolute tw-right-[16px] tw-top-[16px] tw-flex tw-h-[32px] tw-w-[32px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-brand-white"
        onClick={handleDismiss}
        type="button"
      >
        <XIcon />
      </button>

      <div className="tw-flex tw-flex-col md:tw-flex-row-reverse md:tw-gap-16 lg:tw-flex-col-reverse tw-items-center xl:tw-flex-row">
        <img
          alt=""
          aria-hidden="true"
          className="tw-mx-auto tw-block tw-w-full md:tw-w-[50%] lg:tw-hidden"
          src={`${FSV_HOST_GUIDE_IMAGE_BASE},w_800/${FSV_HOST_GUIDE_IMAGE_ID}`}
        />

        <div className="tw-flex tw-flex-1 tw-flex-col md:tw-gap-[16px] lg:tw-pr-0">
          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <Text
              element="h3"
              typeface="headline-3-m"
              className="tw-mt-16 tw-mb-0 tw-text-[24px] tw-font-semibold tw-leading-[32px] tw-text-brand-white"
            >
              <FormattedMessage
                id="host_hub.fsv_host_guide.title"
                defaultMessage="{newLabel} Tools to Help You Book More Events"
                description="FSV Host Guide banner heading on Host Hub. {newLabel} is the highlighted 'New:' label (host_hub.fsv_host_guide.title_new_label)."
                values={{
                  newLabel: (
                    <span className="tw-text-purple-400">
                      <FormattedMessage
                        id="host_hub.fsv_host_guide.title_new_label"
                        defaultMessage="New:"
                        description="Highlighted label shown before the FSV Host Guide banner heading on Host Hub."
                      />
                    </span>
                  ),
                }}
              />
            </Text>
            <Text
              element="p"
              className="tw-mb-24 tw-text-brand-white sm:tw-max-w-[400px]"
            >
              <FormattedMessage
                id="host_hub.fsv_host_guide.desc"
                defaultMessage="Your venue is enrolled in an exclusive program for venues that serve food or drinks. Learn more about customizing pricing, how payments work, and driving more demand for private events from our host guide."
              />
            </Text>
          </div>
          <div className="tw-flex">
            <Button
              appearance="purple"
              className="tw-w-full md:tw-w-auto lg:tw-w-full xl:tw-w-auto md:tw-mb-16"
              onClick={handleCtaClick}
              variant="default"
            >
              <Text typeface="body-2-m">
                <FormattedMessage
                  id="host_hub.fsv_host_guide.cta_btn"
                  defaultMessage="Start increasing booking revenue"
                />
              </Text>
            </Button>
          </div>
        </div>

        <img
          alt=""
          aria-hidden="true"
          className="tw-relative tw-hidden tw-max-w-[300px] lg:tw-block xl:tw--right-32"
          src={`${FSV_HOST_GUIDE_IMAGE_BASE},w_600/${FSV_HOST_GUIDE_IMAGE_ID}`}
        />
      </div>
    </div>
  );
};

export default FSVHostGuideBanner;
